<template>
  <ul class="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
    <li v-for="(view, index) in shots" :key="view.key">
      <button
        type="button"
        class="block w-full appearance-none rounded-lg border-0 bg-white p-0 text-left"
        :class="interactive ? 'cursor-pointer' : 'cursor-default'"
        :disabled="!interactive"
        @click="handleSelect(view.key)"
      >
        <div
          class="relative aspect-video overflow-hidden rounded-t-lg bg-obsidian-50 text-obsidian-300"
        >
          <img
            v-if="getImageUrl(view)"
            :src="getImageUrl(view)"
            alt=""
            class="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <InspectionShotOverlay v-else :shot-key="view.key" still />
        </div>
        <div class="flex items-start justify-between gap-2 p-2">
          <div class="min-w-0">
            <p class="truncate">{{ index + 1 }}. {{ getShotLabel(view.key, fuelType) }}</p>
            <p v-if="view.shot?.skipReason" class="line-clamp-2 text-sm text-obsidian-600">
              {{ view.shot.skipReason }}
            </p>
          </div>
          <Badge v-if="view.state !== 'open'" :variant="getBadge(view).variant" class="shrink-0">
            {{ t(getBadge(view).messageKey) }}
          </Badge>
        </div>
      </button>
    </li>
  </ul>
</template>

<script setup lang="ts">
import type { InspectionShotKey } from '@carvia/ros-client-types';
import { useI18n } from 'vue-i18n';
import Badge from '@/components/Badge.vue';
import type { FuelType } from '@/entities/cars/fuel-type.enum';
import InspectionShotOverlay from './InspectionShotOverlay.vue';
import { getShotImageUrl, type InspectionShotView } from './inspection-state';
import { useShotLabel } from './use-shot-label';

const props = defineProps<{
  shots: InspectionShotView[];
  /** Object URLs of photos still on this device, keyed by shot key. */
  localPreviews?: Record<string, string>;
  fuelType?: FuelType | null;
  interactive?: boolean;
}>();

const emit = defineEmits<{
  (e: 'select', shotKey: InspectionShotKey): void;
}>();

const { t } = useI18n();
const { getShotLabel } = useShotLabel();

const BADGES = {
  captured: { variant: 'success', messageKey: 'captured' },
  uploading: { variant: 'info', messageKey: 'uploading' },
  skipped: { variant: 'warning', messageKey: 'skipped' },
} as const;

const getBadge = (view: InspectionShotView) => {
  if (view.state === 'skipped') return BADGES.skipped;
  return view.shot?.isImagePending ? BADGES.uploading : BADGES.captured;
};

const getImageUrl = (view: InspectionShotView) => getShotImageUrl(view, props.localPreviews);

const handleSelect = (shotKey: InspectionShotKey) => {
  if (props.interactive) emit('select', shotKey);
};
</script>

<i18n lang="json">
{
  "en": { "captured": "Captured", "uploading": "Uploading", "skipped": "Skipped" },
  "de": { "captured": "Erfasst", "uploading": "Lädt hoch", "skipped": "Übersprungen" },
  "es": { "captured": "Hecha", "uploading": "Subiendo", "skipped": "Omitida" }
}
</i18n>
